/**
 * TCS — Escape dismisses the desktop mega-menu panel.
 *
 * WHY THIS EXISTS
 * Horizon's <header-menu> opens a submenu panel on hover AND on keyboard
 * focus (`on:focus="/activate"`), but assets/header-menu.js binds no keydown
 * handler anywhere, so once a keyboard user focuses a top-level item the
 * panel cannot be dismissed without moving focus off it. That fails
 * WCAG 2.1 SC 1.4.13 (Content on Hover or Focus — Dismissible), which
 * requires content shown on hover/focus to be dismissable WITHOUT moving
 * pointer hover or keyboard focus.
 *
 * Measured on the WIP code 2026-08-28 (Playwright, pointer parked away from
 * the header): panels open at baseline 0, focus "Makeup" -> 1 open, press
 * Escape -> still 1 open. Horizon's own mobile drawer handles Escape
 * correctly, so this gap is specific to the desktop panel and is upstream
 * behaviour — not introduced by any TCS change.
 *
 * HOW IT WORKS — and why not a core edit
 * The <li class="menu-list__list-item"> carries Horizon's declarative
 * `on:blur="/deactivate"` binding. Dispatching a real FocusEvent('blur')
 * with relatedTarget null on that element routes through Horizon's OWN
 * public deactivate() path: with no relatedTarget, its isMovingWithinMenu /
 * isMovingToSubmenu / isMovingToOverflowMenu guards are all false, so it
 * falls through to the immediate #deactivate(). We ASK Horizon to close and
 * accept its answer; nothing here overrides what Horizon decides.
 *
 * Focus: never moved when it is outside the menu, or on a control that stays
 * visible after the close (More, a top-level trigger). When it is inside
 * content the close hides (a link in a panel or in the More overflow), it
 * first moves to the trigger that owns that content (More for the overflow,
 * the item's top-level link for a panel), the rule upstream Horizon 4.1.4+
 * follows in #focusTargetAfterClose. Otherwise the browser drops it to <body>.
 * If Horizon refuses the close, focus goes back to where it was.
 *
 * Two listeners, one layer per Escape:
 * - focus inside <header-menu>: capture phase, so the panel closes even if
 *   something inside it stops the event on the way up;
 * - focus elsewhere (a panel opened by hover): bubble phase, so a component
 *   that already handled Escape (dialog.js calls preventDefault and closes)
 *   wins; skipped entirely while a dialog or drawer is open.
 * preventDefault only when a visible panel was actually closed.
 *
 * V01-02: Horizon 4.1.3 #deactivate() refuses to close while the pointer is
 * over the More overflow panel. The owned header-menu.js dismiss({ force: true })
 * API is used only after the normal blur close request leaves More open.
 *
 * COUPLING NOTE: assets/header-menu.js, Horizon 4.1.3, with TCS dismiss({ force })
 * API. Relies on:
 * - li.menu-list__list-item with on:focus="/activate" and on:blur="/deactivate"
 *   (blocks/_header-menu.liquid);
 * - public deactivate(event) falling through to #deactivate() when relatedTarget
 *   is null (header-menu.js:299-317);
 * - public dismiss({ force: true }) closing the active More panel after the
 *   normal blur path refused because the overflow menu is hovered;
 * - activate() being a no-op for the already-active item (:194). Moving focus
 *   to a trigger that is not active runs activate() (it also dispatches
 *   MegaMenuHoverEvent), so a panel may re-open and close within one Escape;
 * - [ref="menuitem"][aria-expanded] on each item's trigger, More at
 *   [slot="more"], overflowed items at [slot="overflow"], and a panel at
 *   li > .menu-list__submenu;
 * - the hover check in #deactivate() (:327), bypassed only through the public
 *   dismiss({ force: true }) fallback described above.
 * scripts/tests/tcs-mega-menu-dismiss-coupling.test.mjs fails when the
 * public dismiss interface changes. On an upgrade to 4.1.4+, delete the
 * focus-inside path (upstream #onKeydown owns it); keep only the focus-outside
 * path, rebuilt on upstream's public API (shopify-cto 2026-09-11).
 *
 * This ships as a TCS asset loaded from snippets/tcs-head.liquid. V01-02 also
 * depends on a narrow Horizon-owned dismiss({ force }) hook; keep that owned
 * scope tied to Escape dismiss for the hovered More overflow panel only.
 */
(() => {
  const ITEM = '.menu-list__list-item';
  const TRIGGER = ':scope > [ref="menuitem"]';
  // Layers that handle Escape themselves: the mobile drawer (collapses an open
  // accordion first, then closes the drawer) and every dialog.
  const LAYER = '.menu-drawer, header-drawer, dialog, [role="dialog"], [aria-modal="true"]';
  const OPEN_LAYER = 'dialog[open], header-drawer details[open], [aria-modal="true"]';

  // Rendered, with a box, and not visibility:hidden. No checkVisibility():
  // Safari < 17.4 lacks it and Horizon still supports Safari 16.
  const isShown = (el) =>
    Boolean(el) &&
    el.getClientRects().length > 0 &&
    el.getBoundingClientRect().height > 0 &&
    getComputedStyle(el).visibility === 'visible';

  /**
   * The list item that owns the visibly open panel of this menu: the More
   * item for the overflow panel, otherwise the expanded item. Null when
   * nothing is shown, e.g. a hovered item without a submenu.
   */
  const openOwner = (menu) => {
    for (const item of menu.querySelectorAll(ITEM)) {
      if (item.querySelector(TRIGGER)?.getAttribute('aria-expanded') !== 'true') continue;
      if (item.slot === 'more' || item.slot === 'overflow') {
        const overflowShown = [
          ...menu.querySelectorAll(`${ITEM}[slot="overflow"] > [ref="menuitem"]`),
        ].some(isShown);
        const more = menu.querySelector(`${ITEM}[slot="more"]`);
        if (overflowShown && more) return more;
      } else if (isShown(item.querySelector(':scope > .menu-list__submenu'))) {
        return item;
      }
    }
    return null;
  };

  /** Ask Horizon to close through its public deactivate(); true if it did. */
  const requestClose = (menu, owner) => {
    owner.dispatchEvent(new FocusEvent('blur', { relatedTarget: null }));
    if (openOwner(menu) === owner && owner.slot === 'more') {
      menu.dismiss?.({ force: true });
    }
    return !openOwner(menu);
  };

  // Focus inside the menu.
  document.addEventListener(
    'keydown',
    (event) => {
      // In the capture phase this only sees handlers registered at
      // window-capture level, so it is not the real protection; kept so such a
      // handler that already claimed Escape still wins.
      if (event.key !== 'Escape' || event.defaultPrevented) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      const menu = target.closest('header-menu');
      if (!menu || target.closest(LAYER)) return;

      const owner = openOwner(menu);
      if (!owner) return;

      // Focus inside content the close hides moves to its owning trigger first.
      const item = target.closest(ITEM);
      const inOverflow = item?.slot === 'overflow' || item?.slot === 'more';
      const keep = inOverflow
        ? menu.querySelector(`${ITEM}[slot="more"] > [ref="menuitem"]`)
        : item?.querySelector(TRIGGER);
      const moved = keep instanceof HTMLElement && keep !== target;
      if (moved) keep.focus();

      if (requestClose(menu, owner)) {
        event.preventDefault();
      } else if (moved && target instanceof HTMLElement) {
        // Horizon refused, so the panel stays open and the focused control stays
        // visible: put focus back.
        target.focus();
      }
    },
    true,
  );

  // Focus elsewhere: a panel opened by hover. Focus is not moved.
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || event.defaultPrevented) return;

    const target = event.target;
    if (target instanceof Element && (target.closest('header-menu') || target.closest(LAYER)))
      return;
    if ([...document.querySelectorAll(OPEN_LAYER)].some((el) => el.getClientRects().length > 0))
      return;

    for (const menu of document.querySelectorAll('header-menu')) {
      const owner = openOwner(menu);
      if (!owner) continue;
      if (requestClose(menu, owner)) event.preventDefault();
      return;
    }
  });
})();
